import { StandardEvents } from '@shopify/events';

const SUFFIX_CLASS = 'kit-savings__suffix';

/**
 * Muestra el % de ahorro de la variante seleccionada (bloque "Ahorro del kit") y lo añade
 * como texto al final del botón de añadir al carrito y al botón de la barra sticky de su sección.
 * Los botones se vuelven a renderizar al cambiar de variante, así que se vigilan con un
 * MutationObserver y el texto se repone cuando desaparece.
 */
class KitSavings extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    if (!this.#section) return;

    this.#section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#observer = new MutationObserver(() => this.#syncButtons());
    this.#observer.observe(this.#section, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    this.#syncButtons();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #percent() {
    return Number(this.dataset.percent) || 0;
  }

  /** @returns {HTMLButtonElement[]} */
  get #mainButtons() {
    if (this.dataset.inButton !== 'true' || !this.#section) return [];
    return [...this.#section.querySelectorAll('.product-details button[name="add"]')];
  }

  /** @returns {Element[]} */
  get #stickyTargets() {
    if (this.dataset.inSticky !== 'true' || !this.#section) return [];
    return [...this.#section.querySelectorAll('.sticky-add-to-cart__button .add-to-cart-text__content')];
  }

  #syncButtons() {
    const label = this.dataset.buttonLabel;
    const percent = this.#percent;

    for (const button of this.#mainButtons) {
      this.#setSuffix(button, button.disabled ? 0 : percent, label, button.querySelector('.add-to-cart__added'));
    }

    for (const target of this.#stickyTargets) {
      const button = target.closest('button');
      this.#setSuffix(target, button?.disabled ? 0 : percent, label, null);
    }
  }

  /**
   * @param {Element} container
   * @param {number} percent
   * @param {string | undefined} label
   * @param {Element | null} before
   */
  #setSuffix(container, percent, label, before) {
    const existing = container.querySelector(`:scope > .${SUFFIX_CLASS}`);

    if (percent <= 0 || !label) {
      existing?.remove();
      return;
    }

    if (existing) {
      if (existing.textContent !== label) existing.textContent = label;
      return;
    }

    const suffix = document.createElement('span');
    suffix.className = SUFFIX_CLASS;
    suffix.textContent = label;
    container.insertBefore(suffix, before);
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const updated = detail?.html?.querySelector(`kit-savings[data-block-id="${this.dataset.blockId}"]`);
        if (!updated) return;

        this.dataset.percent = updated.dataset.percent;
        this.dataset.buttonLabel = updated.dataset.buttonLabel;

        const badge = this.querySelector('.kit-savings__badge');
        const newBadge = updated.querySelector('.kit-savings__badge');
        if (badge && newBadge) badge.replaceWith(newBadge.cloneNode(true));

        this.#syncButtons();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kit-savings] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('kit-savings')) {
  customElements.define('kit-savings', KitSavings);
}
